import { useEffect, useRef, useState, ChangeEvent, MouseEvent } from 'react';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux';
import { setDestination, setOrigin } from '@/redux/bookingSlice';
import { ICity } from '@/types/types';
import LocationIcon from '@/assets/icons/location.svg?react';
import useClickOutside from '@/hooks/useClickOutside';
import arrowDown from '@/assets/icons/arrow-down.svg';
import PlanSVG from '../planSVG/PlanSVG';
import styles from './selectors.module.scss';
import APIKit from '@/utils/APIKit';
import adaptCities from '@/utils/adapters/citiesAdapter';

interface IProps {
    type: 'origin' | 'destination';
    location?: string;
}

const normalizeString = (string: string) => {
    const normalizedString = string
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

    return normalizedString;
};

const CitySelector = ({ location = 'home', type }: IProps) => {
    const origin = useAppSelector((state) => state.booking.origin);
    const destination = useAppSelector((state) => state.booking.destination);
    const [availableCities, setAvailableCities] = useState<ICity[]>([]);
    const [isActive, setIsActive] = useState(false);
    const selectorRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const isHomePage = location === 'home';
    const dispatch = useAppDispatch();
    const { t, i18n } = useTranslation();
    const isOrigin = type === 'origin';

    useEffect(() => {
        if (isActive) inputRef.current?.focus();
    }, [isActive]);

    useClickOutside(selectorRef, () => {
        if (isActive) {
            setIsActive(false);
        }
    });

    useEffect(() => {
        if (origin.id) {
            APIKit.get(`/places/cities/${origin.id}`).then((res) => {
                const cities = adaptCities([res.data]);
                dispatch(setOrigin(cities[0]));
            });
        }

        if (destination.id) {
            APIKit.get(`/places/cities/${destination.id}`).then((res) => {
                const cities = adaptCities([res.data]);
                dispatch(setDestination(cities[0]));
            });
        }
    }, [i18n.language]);

    const onSelectorClick = (e: MouseEvent<HTMLButtonElement>) => {
        const currentTarget = e.target;
        const inputElement = inputRef?.current;
        const eventOutsideInput = currentTarget !== inputElement;
        const eventWasTriggerByKeyboard = e.detail === 0;

        if (eventOutsideInput && !eventWasTriggerByKeyboard) {
            return setIsActive(!isActive);
        }
        if (isActive) return false;

        return setIsActive(true);
    };

    const onItemClick = (city: ICity) => {
        if (isOrigin) {
            dispatch(setOrigin(city));
        } else {
            dispatch(setDestination(city));
        }
        setIsActive(false);
        setAvailableCities([]);
    };

    const getNewCities = (inputValue: string) => {
        APIKit.get(`/places/cities/?translations__name__icontains=${inputValue}`).then((res) => {
            const cities = adaptCities(res.data.results);
            setAvailableCities(cities);
        });
    };

    const onInputChange = debounce((e: ChangeEvent<HTMLInputElement>) => {
        e.persist();
        const inputValue = normalizeString(e.target.value);

        if (inputValue === '') return setAvailableCities([]);

        return getNewCities(inputValue);
    }, 300);

    const selectedCity = isOrigin ? origin : destination;
    const showCitiesList = isActive && availableCities.length > 0;

    const labelText = isOrigin ? t('departure') : t('destination');
    const countryName = selectedCity.countryName !== '' ? `, ${selectedCity.countryName}` : '';
    const selectorText = selectedCity.id !== 0 ? `${selectedCity.name}${countryName}` : labelText;

    const selectorTextClassName = selectedCity.id !== 0 ? styles.text : `${styles.text} ${styles.placeholder}`;
    const selectorClassName =
        isActive && showCitiesList ? `${styles.selector} ${styles.selectorActive}` : styles.selector;
    const containerStyles = isHomePage
        ? `${styles.selectorContainer} ${styles.originSelector}`
        : `${styles.selectorContainer} ${styles.originSelector} ${styles.fullWidth}`;

    const citiesToShow = availableCities;

    return (
        <div className={containerStyles} ref={selectorRef}>
            <button className={selectorClassName} onClick={(e) => onSelectorClick(e)}>
                <PlanSVG svg={<LocationIcon />} className={styles.icon} />

                {isActive && (
                    <input
                        className={styles.input}
                        onChange={onInputChange}
                        ref={inputRef}
                        placeholder={t('writeACity')}
                    />
                )}
                {!isActive && <p className={selectorTextClassName}>{selectorText}</p>}

                <div className={styles.arrow}>
                    <img src={arrowDown} alt="" className={isActive ? styles.arrowActive : ''} />
                </div>
            </button>
            {showCitiesList && (
                <SelectorOptions cities={citiesToShow} onItemClick={onItemClick} inputValue={inputRef.current?.value} />
            )}
        </div>
    );
};

interface IOptionsProps {
    cities: ICity[];
    onItemClick: any;
    inputValue: string | undefined;
}

const SelectorOptions = ({ cities, onItemClick, inputValue }: IOptionsProps) => {
    const hasCities = cities.length > 0;
    const inputIsEmpty = inputValue === '' || inputValue === undefined;
    const { t, i18n } = useTranslation();
    const lang = i18n.language;

    const citiesByCountryName = [...cities].sort(
        (a, b) =>
            a.countryName.charCodeAt(0) - b.countryName.charCodeAt(0) || a.name.charCodeAt(0) - b.name.charCodeAt(0),
    );

    const putFirstTheLanguageCities = () => {
        const selectedLanguageCities = citiesByCountryName.filter((city) => city.countryCode.toLowerCase() === lang);
        const otherCities = citiesByCountryName.filter((city) => city.countryCode.toLowerCase() !== lang);

        const newCitiesList = [...selectedLanguageCities, ...otherCities];
        return newCitiesList;
    };

    const citiesOrderedByLanguage = putFirstTheLanguageCities();

    return (
        <div className={`${styles.selectorOptions} ${styles.originSelectorOptions}`}>
            <ul>
                {hasCities &&
                    citiesOrderedByLanguage.map((city: ICity, index: number) => {
                        const countryName = city.countryName !== '' ? `, ${city.countryName}` : '';

                        return (
                            // eslint-disable-next-line react/no-array-index-key
                            <li key={`${city.id}${city.name}${index}`}>
                                <button onClick={() => onItemClick(city)}>
                                    <PlanSVG svg={<LocationIcon />} className={styles.icon} />
                                    {`${city.name}${countryName}`}
                                </button>
                            </li>
                        );
                    })}

                {!hasCities && !inputIsEmpty && <span className={styles.notFound}>{t('cityNotFound')}</span>}

                {/* {!hasCities && inputIsEmpty && <span className={styles.notFound}>Escribe una ciudad</span>} */}
            </ul>
        </div>
    );
};

export default CitySelector;
